import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { ServiceDetailPageContainer } from './ServiceDetailPage';
import { FaPaperPlane } from 'react-icons/fa';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const ServiceImage = styled.img`
  max-width: 200px;
  height: auto;
  border-radius: 0;
  margin: 0.5rem 0 1rem 2rem;
  box-shadow: none;
  float: right;

  @media (max-width: 768px) {
    float: none;
    margin: 1rem auto;
    max-width: 80%;
    display: block;
  }
`;

const ContactSection = styled.section`
  padding: 4rem 2rem;
  background: linear-gradient(45deg, #00B4DB, #007bff);
  text-align: center;
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 250px;
  color: white;
  position: relative;
  overflow: hidden;
  margin-top: 8rem;
  border-radius: 20px;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 30%;
    height: 100%;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.15), transparent);
    transform: skewX(-20deg) translateX(30%);
    transform-origin: top right;
    pointer-events: none;
    z-index: 0;
  }
`;

const ContactContent = styled.div`
  position: relative;
  z-index: 1;
  max-width: 600px;
  width: 100%;
`;

export const ContactTitle = styled.h2`
  color: #fff !important;
  margin-top: 0 !important;
`;

const ContactForm = styled.form`
  display: flex;
  gap: 1rem;
  justify-content: center;
  align-items: center;
  flex-wrap: wrap;
`;

const ContactInput = styled.input`
  padding: 1rem 1.5rem;
  border: none;
  border-radius: 10px;
  font-size: 1rem;
  flex: 1;
  min-width: 200px;
  background: rgba(255, 255, 255, 0.9);
  color: #1f2937;

  &::placeholder {
    color: #6b7280;
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.5);
  }
`;

const SubmitButton = styled.button`
  background: #00B4DB;
  color: white;
  border: none;
  padding: 1rem 1.5rem;
  font-size: 1.2rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 8px rgba(0, 180, 219, 0.2);

  &:hover {
    background: #007bff;
    transform: scale(1.02);
    box-shadow: 0 4px 12px rgba(0, 180, 219, 0.3);
  }

  svg {
    margin: 0;
  }
`;

function GoogleBusiness() {
  const [formData, setFormData] = useState({
    name: '',
    phone: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    if (name === 'phone') {
      // Only allow numbers and limit to exactly 11 digits
      const numbersOnly = value.replace(/[^\d]/g, '');
      if (numbersOnly.length <= 11) {
        setFormData({
          ...formData,
          [name]: numbersOnly
        });
      }
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.phone.length !== 11) {
      alert('Telefon numarası 11 haneli olmalıdır.');
      return;
    }

    try {
      const templateParams = {
        name: formData.name,
        phone: formData.phone,
        time: new Date().toLocaleString('tr-TR')
      };

      await emailjs.send(
        'service_771tgg7',
        'template_w676du8',
        templateParams,
        'xjG6_a9zdp6ZU1zHp'
      );

      alert('Başarılı');
      setFormData({ name: '', phone: '' });
    } catch (error) {
      alert('Bir hata oluştu');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <ServiceDetailPageContainer>
      <h1>Google İşletme Profili Hizmetleri</h1>
      <p>Google My Business hesabınızı oluşturup yöneterek, yerel aramalarda öne çıkmanızı sağlıyoruz. Profesyonel ekibimiz, işletmenizin Google'daki görünürlüğünü artırarak, potansiyel müşterilerinize daha kolay ulaşmanızı sağlıyor.</p>
      
      <ServiceImage src="/media/demirer-ajans-1.png" alt="Google İşletme Görseli 1" />

      <h2>Neden Google İşletme Profili Önemlidir?</h2>
      <p>Google İşletme Profili, yerel aramalarda işletmenizin görünürlüğünü artıran en etkili araçlardan biridir. Doğru yönetilen bir Google İşletme Profili, potansiyel müşterilerin işletmenizi bulmasını kolaylaştırır, müşteri yorumlarınızı yönetmenizi sağlar ve işletmenizin güvenilirliğini artırır.</p>

      <ServiceImage src="/media/demirer-ajans-2.png" alt="Google İşletme Görseli 2" />

      <h3>Google İşletme Profili Hizmetlerimiz Neleri Kapsar?</h3>
      <ul>
        <li><b>Profil Oluşturma ve Optimizasyon:</b> Google İşletme Profilinizi oluşturup, en iyi şekilde optimize ediyoruz.</li>
        <li><b>İçerik Yönetimi:</b> Profilinizdeki bilgileri güncel tutuyor, etkileyici içerikler oluşturuyoruz.</li>
        <li><b>Fotoğraf ve Görsel Yönetimi:</b> Profesyonel fotoğraflar ve görseller ile profilinizi zenginleştiriyoruz.</li>
        <li><b>Müşteri Yorumları Yönetimi:</b> Müşteri yorumlarınızı yönetip, olumlu geri bildirimleri artırıyoruz.</li>
        <li><b>Yerel SEO Optimizasyonu:</b> Yerel aramalarda üst sıralarda yer almanızı sağlıyoruz.</li>
        <li><b>Performans Analizi:</b> Profilinizin performansını takip edip, detaylı raporlar sunuyoruz.</li>
        <li><b>Rekabet Analizi:</b> Rakip işletmelerin Google'daki varlığını analiz edip, stratejiler geliştiriyoruz.</li>
        <li><b>Sürekli Destek:</b> Profilinizin güncel ve etkili kalması için sürekli destek sağlıyoruz.</li>
      </ul>

      <ServiceImage src="/media/demirer-ajans-3.png" alt="Google İşletme Görseli 3" />

      <h2>Google'da Öne Çıkın</h2>
      <p>Google İşletme Profili yönetimi, düzenli ilgi ve uzmanlık gerektirir. Profesyonel ekibimiz, işletmenizin Google'daki varlığını en iyi şekilde yöneterek, potansiyel müşterilerinize ulaşmanızı kolaylaştırıyor. Her aşamada sizi bilgilendiriyor, gerekli güncellemeleri yapıyor ve profilinizin performansını artırıyoruz.</p>
      <p>Google'da öne çıkmak ve yerel aramalarda başarılı olmak için profesyonel Google İşletme Profili hizmetlerimizden yararlanın. Dijital varlığınızı güçlendirerek, işletmenizin online başarısını artırın.</p>

      <h2>Google İşletme Kaydı Hizmetlerimiz</h2>
      <p>Google İşletme Kaydı hizmetlerimiz, işletmenizin Google'da görünürlüğünü artırmak için tasarlanmıştır. Uzman ekibimiz, işletmenizin Google Haritalar ve Google Arama'da en iyi şekilde temsil edilmesini sağlar.</p>
      <p>Profesyonel optimizasyon ve yönetim hizmetleriyle, işletmenizin online varlığını güçlendiriyor ve potansiyel müşterilere ulaşmanızı kolaylaştırıyoruz. İşletmenizin dijital görünürlüğünü artırmak için bize güvenebilirsiniz.</p>

      <ContactSection>
        <ContactContent>
          <ContactTitle>Sizi Arayalım</ContactTitle>
          <ContactForm onSubmit={handleSubmit}>
            <ContactInput 
              type="text" 
              name="name"
              placeholder="Adınız"
              value={formData.name}
              onChange={handleInputChange}
              required 
            />
            <ContactInput 
              type="tel" 
              name="phone"
              placeholder="Telefon Numaranız (11 Haneli)"
              value={formData.phone}
              onChange={handleInputChange}
              pattern="[0-9]*"
              inputMode="numeric"
              maxLength="11"
              minLength="11"
              required 
            />
            <SubmitButton
              type="submit"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <FaPaperPlane />
            </SubmitButton>
          </ContactForm>
        </ContactContent>
      </ContactSection>
    </ServiceDetailPageContainer>
  );
}

export default GoogleBusiness; 